import angular from 'angular';
import {
    formatMovimentacaoReferenciaLabel,
    formatMovimentacaoValorLabel,
    formatMovimentacaoVigenciaLabel
} from '../../../../Movimentacoes/helpers/movimentacoes-value.helpers';
import { ModalConfirmService } from '../../../../../shared/components/modal-confirm/modal.confirm.service';
import {
    IMovimentacaoAdmissao,
    IMovimentacaoAdmissaoCreatePayload,
    IMovimentacaoAdmissaoFormModalResult,
    IMovimentacaoAdmissaoUpdatePayload,
    MovimentacaoAdmissaoSituacao
} from '../../models/movimentacoes-admissoes.model';
import { IMovimentacaoAdmissaoFormModel } from '../form/movimentacoes-admissoes-form.model';
import {
    IMovimentacaoAdmissaoRow,
    IPendingMovimentacaoChange,
} from './movimentacoes-admissoes.model';
import { MovimentacoesAdmissoesFormModalService } from '../../modals/form/movimentacoes-admissoes-form.modal.service';
import { MovimentacoesAdmissoesService } from '../../movimentacoes-admissoes.service';

export class MovimentacoesAdmissoesController implements angular.IController {

    static $inject = [
        '$rootScope',
        '$q',
        '$filter',
        'MovimentacoesAdmissoesService',
        'MovimentacoesAdmissoesFormModalService',
        'ModalConfirmService',
        'NewToaster'
    ];

    private static readonly FLUSH_CONCLUIDO_EVENT = 'meurh_movimentacoes_admissoes_flush_concluido';

    public entity: any;
    public initOpen = false;
    public readonly = false;
    public movimentacoes: IMovimentacaoAdmissao[] = [];
    public visibleMovimentacoesList: IMovimentacaoAdmissaoRow[] = [];
    public hasVisibleActions = false;
    public tableColspan = 6;
    public busy = false;
    public loaded = false;

    /*
        alteração pendente (criação ou edição) mantida em memória até o flush,
        disparado quando a admissão é confirmada/salva. Guarda o payload já
        normalizado para o backend e o model do formulário (com os objetos
        completos de evento/lotação) para reconstruir a exibição da linha.
    */
    private pendingCreates = new Map<string, IPendingMovimentacaoChange>();
    private pendingUpdates = new Map<string, IPendingMovimentacaoChange>();
    private pendingDeleteIds = new Set<string>();
    private tempIdCounter = 0;
    private resolvedSolicitacao: string | null = null;
    private offListeners: Array<() => void> = [];

    constructor(
        private $rootScope: angular.IRootScopeService & {
            temPermissao: (permissao: string | string[]) => boolean;
            liberadoTela: (liberacao: string) => boolean;
        },
        private $q: angular.IQService,
        private $filter: angular.IFilterFunction,
        private MovimentacoesAdmissoesService: MovimentacoesAdmissoesService,
        private MovimentacoesAdmissoesFormModalService: MovimentacoesAdmissoesFormModalService,
        private ModalConfirmService: ModalConfirmService,
        private NewToaster: {
            pop: (opts: {
                type: string; title: string; body?: string, timeout?: number
            }) => void
        },
    ) { }

    public $onInit(): void {
        this.initOpen = !!this.initOpen;
        this.load();

        const eventos = [
            'meurh_solicitacoesadmissoescompleta_submitted',
            'meurh_solicitacoesadmissoescompleta_preliminar_submitted',
            'meurh_solicitacoesadmissoescompleta_encaminhado',
            'meurh_solicitacoesadmissoesestagiario_submitted',
            'meurh_solicitacoesadmissoesestagiario_encaminhado'
        ];

        this.offListeners = eventos.map((evento) =>
            this.$rootScope.$on(evento, (_event, args) => this.handleAdmissaoSubmitted(args))
        );

        this.offListeners.push(
            this.$rootScope.$on(
                MovimentacoesAdmissoesController.FLUSH_CONCLUIDO_EVENT,
                (_event, args: { solicitacao?: string }) => {
                    if (args?.solicitacao && args.solicitacao === this.getSolicitacao()) {
                        this.load();
                    }
                }
            )
        );
    }

    public $onChanges(changes: angular.IOnChangesObject): void {
        if (changes.entity && !changes.entity.isFirstChange()) {
            const novaSolicitacao = changes.entity.currentValue?.solicitacao;

            /*
                o componente foi revinculado a outra solicitação: o guid capturado
                do broadcast e as pendências locais pertenciam à solicitação
                anterior e não podem vazar para a nova.
            */
            if (this.resolvedSolicitacao &&
                typeof novaSolicitacao === 'string' && novaSolicitacao.trim() &&
                novaSolicitacao !== this.resolvedSolicitacao) {
                this.resolvedSolicitacao = null;
                this.limparPendencias();
            }

            this.load();
        }

        if (changes.readonly && !changes.readonly.isFirstChange()) {
            this.syncTableState();
        }
    }

    public $onDestroy(): void {
        this.offListeners.forEach((off) => off());
    }

    public shouldRender(): boolean {
        return this.hasFeatureAccess() && !this.isRetificacao();
    }

    public canCreate(): boolean {
        return this.canMutateSolicitacao();
    }

    public openCreate(): void {
        if (!this.canCreate()) {
            return;
        }

        this.openFormModal(null);
    }

    public openEdit(movimentacao: IMovimentacaoAdmissao): void {
        if (!this.canEdit(movimentacao)) {
            return;
        }

        this.openFormModal(movimentacao);
    }

    public excluir(movimentacao: IMovimentacaoAdmissao): void {
        if (!this.canDelete(movimentacao)) {
            return;
        }

        const customText = {
            title: 'Excluir movimentação',
            confirmText: 'Tem certeza que deseja excluir esta movimentação?',
            closeButton: 'Cancelar',
            confirmButton: 'Sim, excluir'
        };

        this.ModalConfirmService.open(movimentacao, 'movimentação', false, customText)
            .result
            .then(() => {
                const identifier = movimentacao.movimentacaosolicitacao;

                if (this.pendingCreates.has(identifier)) {
                    this.pendingCreates.delete(identifier);
                } else {
                    this.pendingUpdates.delete(identifier);
                    this.pendingDeleteIds.add(identifier);
                }

                this.movimentacoes = this.movimentacoes.filter(
                    (item) => item.movimentacaosolicitacao !== identifier
                );
                this.syncTableState();
            })
            .catch(() => undefined);
    }

    private load(): angular.IPromise<IMovimentacaoAdmissao[]> {
        if (!this.shouldRender()) {
            this.movimentacoes = [];
            this.syncTableState();
            this.loaded = true;
            return this.$q.resolve([]);
        }

        if (!this.hasSolicitacao()) {
            this.movimentacoes = this.applyPendingState([]);
            this.syncTableState();
            this.loaded = true;
            return this.$q.resolve(this.movimentacoes);
        }

        this.busy = true;
        return this.MovimentacoesAdmissoesService.list(this.getSolicitacao()!)
            .then((movimentacoes) => {
                this.movimentacoes = this.applyPendingState(movimentacoes || []);
                this.syncTableState();
                this.loaded = true;
                return this.movimentacoes;
            })
            .catch((error): IMovimentacaoAdmissao[] => {
                /*
                    zera a tabela (preservando pendências locais) para não exibir
                    dados possivelmente desatualizados como se fossem correntes.
                */
                this.movimentacoes = this.applyPendingState([]);
                this.syncTableState();
                this.loaded = true;
                this.showError('Não foi possível carregar as movimentações', error);
                return this.movimentacoes;
            })
            .finally(() => {
                this.busy = false;
            }) as angular.IPromise<IMovimentacaoAdmissao[]>;
    }

    private openFormModal(movimentacao: IMovimentacaoAdmissao | null): void {
        const modal = this.MovimentacoesAdmissoesFormModalService.open({
            solicitacao: this.getSolicitacao()!,
            dataadmissao: this.entity?.dataadmissao || null,
            lotacao: this.entity?.lotacao || null,
            movimentacao
        });

        modal.result
            .then((result: IMovimentacaoAdmissaoFormModalResult) => {
                if (movimentacao) {
                    this.stageUpdate(movimentacao, result);
                } else {
                    this.stageCreate(result);
                }
            })
            .catch(() => { /* modal dismissed */ });
    }

    private stageCreate(result: IMovimentacaoAdmissaoFormModalResult): void {
        const tempId = `temp-${++this.tempIdCounter}`;
        const payload = result.payload as IMovimentacaoAdmissaoCreatePayload;

        this.pendingCreates.set(tempId, { payload, model: result.model });
        this.movimentacoes = [...this.movimentacoes, this.buildRow(tempId, payload, result.model)];
        this.syncTableState();
    }

    private stageUpdate(original: IMovimentacaoAdmissao, result: IMovimentacaoAdmissaoFormModalResult): void {
        const identifier = original.movimentacaosolicitacao;
        const payload = result.payload as IMovimentacaoAdmissaoUpdatePayload;

        if (this.pendingCreates.has(identifier)) {
            /*
                o registro ainda não foi enviado ao backend: mantém a pendência
                como criação (com `solicitacao`), e não como edição (que carrega
                `versao` em vez disso e falharia na validação do endpoint de criação).
            */
            this.pendingCreates.set(identifier, { payload: this.toCreatePayload(payload), model: result.model });
        } else {
            this.pendingUpdates.set(identifier, { payload, model: result.model });
        }

        this.movimentacoes = this.movimentacoes.map((item) =>
            item.movimentacaosolicitacao === identifier
                ? this.buildRow(identifier, payload, result.model, item)
                : item
        );
        this.syncTableState();
    }

    private toCreatePayload(payload: IMovimentacaoAdmissaoUpdatePayload): IMovimentacaoAdmissaoCreatePayload {
        return {
            solicitacao: this.getSolicitacao() || '',
            evento: payload.evento,
            lotacao: payload.lotacao,
            valor: payload.valor,
            referencia: payload.referencia,
            vigencia: payload.vigencia,
            vigenciamesfinal: payload.vigenciamesfinal,
            vigenciaanofinal: payload.vigenciaanofinal
        };
    }

    private buildRow(
        identifier: string,
        payload: IMovimentacaoAdmissaoCreatePayload | IMovimentacaoAdmissaoUpdatePayload,
        model: IMovimentacaoAdmissaoFormModel,
        base?: IMovimentacaoAdmissao
    ): IMovimentacaoAdmissao {
        return {
            movimentacaosolicitacao: identifier,
            solicitacao: base?.solicitacao || {
                solicitacao: this.getSolicitacao() || '',
                tiposolicitacao: Number(this.entity?.tiposolicitacao)
            },
            ordem: base?.ordem ?? 0,
            evento: model.evento,
            lotacao: model.lotacao,
            valor: payload.valor,
            referencia: payload.referencia,
            vigencia: payload.vigencia,
            vigenciamesfinal: payload.vigenciamesfinal,
            vigenciaanofinal: payload.vigenciaanofinal,
            situacao: base?.situacao ?? MovimentacaoAdmissaoSituacao.RASCUNHO,
            movimentacao: base?.movimentacao ?? null,
            versao: base?.versao ?? 0
        };
    }

    private applyPendingState(movimentacoes: IMovimentacaoAdmissao[]): IMovimentacaoAdmissao[] {
        const withUpdates = movimentacoes
            .filter((item) => !this.pendingDeleteIds.has(item.movimentacaosolicitacao))
            .map((item) => {
                const pending = this.pendingUpdates.get(item.movimentacaosolicitacao);
                return pending
                    ? this.buildRow(item.movimentacaosolicitacao, pending.payload, pending.model, item)
                    : item;
            });

        const pendingCreateRows = Array.from(this.pendingCreates.entries())
            .map(([tempId, pending]) => this.buildRow(tempId, pending.payload, pending.model));

        return [...withUpdates, ...pendingCreateRows];
    }

    private handleAdmissaoSubmitted(args: { entity?: { solicitacao?: string; situacao?: number | string } }): void {
        const solicitacaoEvento = args?.entity?.solicitacao;
        const solicitacaoVinculada = this.entity?.solicitacao;

        /*
            o guid do evento só é aproveitado quando o binding ainda não tem
            solicitação (tela "Nova Admissão"). Se o componente já está
            vinculado a outra solicitação, o evento não é dele — ignora.
        */
        if (typeof solicitacaoVinculada === 'string' && solicitacaoVinculada.trim() &&
            typeof solicitacaoEvento === 'string' && solicitacaoEvento.trim() &&
            solicitacaoEvento !== solicitacaoVinculada) {
            return;
        }

        if (typeof solicitacaoEvento === 'string' && solicitacaoEvento.trim()) {
            this.resolvedSolicitacao = solicitacaoEvento;
        }

        const situacao = args?.entity?.situacao;
        const foiEnviada = situacao !== undefined && situacao !== null && Number(situacao) === 0;

        this.flushPendingChanges(foiEnviada);
    }

    private flushPendingChanges(foiEnviada: boolean = false): angular.IPromise<void> {
        const creates = Array.from(this.pendingCreates.values());
        const updates = Array.from(this.pendingUpdates.entries());
        const deletes = Array.from(this.pendingDeleteIds);

        if (!creates.length && !updates.length && !deletes.length) {
            return this.$q.resolve();
        }

        /*
            limpa as pendências já no snapshot (e não no retorno de cada
            requisição) para fechar a janela de corrida: um novo evento de save
            chegando com este flush ainda em andamento não pode reler e
            reenviar as mesmas pendências, o que duplicaria registros.
        */
        this.limparPendencias();

        this.busy = true;
        let hadFailure = false;
        const solicitacao = this.getSolicitacao() || '';

        const executarPendencia = (requisicao: angular.IPromise<unknown>): angular.IPromise<void> =>
            requisicao
                .then(() => undefined)
                .catch(() => { hadFailure = true; });

        const requests = [
            ...creates.map((pending) =>
                executarPendencia(this.MovimentacoesAdmissoesService.create({
                    ...(pending.payload as IMovimentacaoAdmissaoCreatePayload),
                    solicitacao
                }))),
            ...updates.map(([identifier, pending]) =>
                executarPendencia(this.MovimentacoesAdmissoesService.update(
                    identifier,
                    pending.payload as IMovimentacaoAdmissaoUpdatePayload
                ))),
            ...deletes.map((identifier) =>
                executarPendencia(this.MovimentacoesAdmissoesService.delete(identifier)))
        ];

        return this.$q.all(requests)
            .then(() => {
                if (hadFailure) {
                    this.NewToaster.pop({
                        type: 'error',
                        title: 'A admissão foi salva, mas algumas movimentações não puderam ser confirmadas',
                        body: 'Revise as pendências na lista de movimentações e tente novamente.',
                        timeout: 10000
                    });
                }

                /*
                    as pré-movimentações recém-criadas nascem em RASCUNHO. Quando a
                    admissão já existia, o backend as envia automaticamente em lote
                    na mesma transação do envio da admissão — mas as que acabaram de
                    ser criadas aqui não existiam ainda naquele momento, então
                    precisam ser enviadas explicitamente para acompanhar a situação
                    da admissão. Isso vale mesmo se alguma outra pendência falhou:
                    as que foram confirmadas com sucesso continuam elegíveis e não
                    devem ficar presas em rascunho por causa de um erro em outro item.
                */
                if (foiEnviada && creates.length && solicitacao) {
                    return this.MovimentacoesAdmissoesService.enviarPorSolicitacao(solicitacao)
                        .catch((error) => {
                            this.showError(
                                'As movimentações foram criadas, mas não foi possível enviá-las automaticamente',
                                error
                            );
                        });
                }

                return undefined;
            })
            .finally(() => {
                this.busy = false;

                /*
                    não chama this.load() diretamente aqui: a tela pode ter
                    navegado para outro lugar (ex.: redirect para "Show" após o
                    envio) antes deste flush terminar, e recarregar esta
                    instância seria inútil nesse caso (ninguém mais a enxerga).
                    Delega o reload inteiramente para quem estiver de fato
                    montado e escutando quando o evento chegar — inclusive
                    esta mesma instância, se ela ainda existir (também está
                    inscrita no evento, então se atualiza sozinha sem duplicar
                    a chamada).
                */
                this.$rootScope.$broadcast(
                    MovimentacoesAdmissoesController.FLUSH_CONCLUIDO_EVENT,
                    { solicitacao }
                );
            }) as angular.IPromise<void>;
    }

    private limparPendencias(): void {
        this.pendingCreates.clear();
        this.pendingUpdates.clear();
        this.pendingDeleteIds.clear();
    }

    private syncTableState(): void {
        this.visibleMovimentacoesList = this.movimentacoes
            .filter((movimentacao) => Number(movimentacao.situacao) !== MovimentacaoAdmissaoSituacao.EXCLUIDA)
            .map((movimentacao) => this.buildRowViewModel(movimentacao));
        this.hasVisibleActions = this.visibleMovimentacoesList.some((row) => row.hasAnyAction);
        this.tableColspan = this.hasVisibleActions ? 7 : 6;
    }

    private buildRowViewModel(movimentacao: IMovimentacaoAdmissao): IMovimentacaoAdmissaoRow {
        const canEdit = this.canEdit(movimentacao);
        const canDelete = this.canDelete(movimentacao);

        return {
            movimentacao,
            eventoNome: movimentacao.evento?.nome || '-',
            eventoCodigo: movimentacao.evento?.codigo || '',
            lotacaoNome: movimentacao.lotacao?.nome || '-',
            lotacaoCodigo: movimentacao.lotacao?.codigo || '',
            valorLabel: formatMovimentacaoValorLabel(movimentacao.valor, movimentacao.evento, this.$filter),
            referenciaLabel: formatMovimentacaoReferenciaLabel(movimentacao.referencia),
            vigenciaLabel: formatMovimentacaoVigenciaLabel(movimentacao, {
                unicaLabel: 'Apenas neste período',
                mensalLabel: 'Sempre'
            }),
            statusLabel: this.getStatusLabel(movimentacao),
            statusClass: this.getStatusClass(movimentacao),
            canEdit,
            canDelete,
            hasAnyAction: canEdit || canDelete
        };
    }

    private getStatusLabel(movimentacao: IMovimentacaoAdmissao): string {
        switch (Number(movimentacao.situacao)) {
            case MovimentacaoAdmissaoSituacao.RASCUNHO:
                return 'Rascunho';
            case MovimentacaoAdmissaoSituacao.ENVIADO:
                return 'Enviado';
            case MovimentacaoAdmissaoSituacao.CONFIRMADO:
                return 'Confirmado';
            case MovimentacaoAdmissaoSituacao.EXCLUIDA:
                return 'Excluído';
            case MovimentacaoAdmissaoSituacao.ERRO:
                return 'Erro';
            default:
                return 'Não informado';
        }
    }

    private getStatusClass(movimentacao: IMovimentacaoAdmissao): string {
        switch (Number(movimentacao.situacao)) {
            case MovimentacaoAdmissaoSituacao.RASCUNHO:
                return 'label-default';
            case MovimentacaoAdmissaoSituacao.ENVIADO:
                return 'label-primary';
            case MovimentacaoAdmissaoSituacao.CONFIRMADO:
                return 'label-success';
            case MovimentacaoAdmissaoSituacao.EXCLUIDA:
                return 'label-default';
            case MovimentacaoAdmissaoSituacao.ERRO:
                return 'label-danger';
            default:
                return 'label-default';
        }
    }

    private canEdit(movimentacao: IMovimentacaoAdmissao): boolean {
        return this.canMutateSolicitacao() && this.isRascunhoOuEnviado(movimentacao);
    }

    private canDelete(movimentacao: IMovimentacaoAdmissao): boolean {
        return !this.readonly &&
            this.hasActionAccess() &&
            this.isRascunhoOuEnviado(movimentacao);
    }

    private canMutateSolicitacao(): boolean {
        return !this.readonly &&
            this.hasActionAccess() &&
            this.isSolicitacaoEditavel();
    }

    private hasFeatureAccess(): boolean {
        return this.$rootScope.liberadoTela('LIBERAR_GESTAO_MOVIMENTACOES') &&
            this.$rootScope.liberadoTela('LIBERAR_MOVIMENTACOES_ADMISSOES') &&
            this.$rootScope.temPermissao('visualizar_movimentacao');
    }

    private hasActionAccess(): boolean {
        return this.$rootScope.temPermissao('criar_movimentacao');
    }

    private isRetificacao(): boolean {
        return !!this.entity?.retificacao;
    }

    private isSolicitacaoEditavel(): boolean {
        const situacao = this.entity?.situacao;
        return situacao === undefined || situacao === null ||
            Number(situacao) === -1 || !!this.entity?._editavel;
    }

    private isRascunhoOuEnviado(movimentacao: IMovimentacaoAdmissao): boolean {
        return [
            MovimentacaoAdmissaoSituacao.RASCUNHO,
            MovimentacaoAdmissaoSituacao.ENVIADO
        ].indexOf(Number(movimentacao.situacao)) !== -1;
    }

    private hasSolicitacao(): boolean {
        return !!this.getSolicitacao();
    }

    private getSolicitacao(): string | null {
        if (this.resolvedSolicitacao) {
            return this.resolvedSolicitacao;
        }

        const solicitacao = this.entity?.solicitacao;
        return typeof solicitacao === 'string' && solicitacao.trim() ? solicitacao : null;
    }

    private showError(title: string, error: any): void {
        this.NewToaster.pop({
            type: 'error',
            title,
            body: error?.data?.message || error?.message,
            timeout: 10000
        });
    }
}
